import { useMemo, useRef, useState } from "react";

export interface CalendarProps {
  // ISO day, "YYYY-MM-DD". Empty means nothing is chosen yet.
  value: string;
  onChange: (day: string) => void;
  min?: string;
  max?: string;
  // Shades the span between this day and the value, for a from/to pair.
  rangeFrom?: string;
  label?: string;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parse(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

// Monday-first, because the books this serves are kept in Europe.
function startOfGrid(month: Date): Date {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const weekday = (first.getDay() + 6) % 7;
  return new Date(first.getFullYear(), first.getMonth(), 1 - weekday);
}

export function Calendar({ value, onChange, min, max, rangeFrom, label = "Choose a day" }: CalendarProps) {
  const selected = parse(value);
  const today = iso(new Date());
  const grid = useRef<HTMLDivElement>(null);
  // The grid follows the chosen day; browsing months and walking days are
  // offsets on top of it, so changing the value needs no effect to sync.
  const [monthOffset, setMonthOffset] = useState(0);
  const [walked, setWalked] = useState("");
  const anchorDay = selected ?? new Date();
  const cursor = useMemo(
    () => new Date(anchorDay.getFullYear(), anchorDay.getMonth() + monthOffset, 1),
    [anchorDay, monthOffset],
  );
  const focused = walked || value || today;

  function setCursorTo(date: Date) {
    setMonthOffset((date.getFullYear() - anchorDay.getFullYear()) * 12 + (date.getMonth() - anchorDay.getMonth()));
  }

  const days = useMemo(() => {
    const start = startOfGrid(cursor);
    return Array.from({ length: 42 }, (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index));
  }, [cursor]);

  const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(cursor);
  const blocked = (day: string) => (min !== undefined && min !== "" && day < min) || (max !== undefined && max !== "" && day > max);
  const spanStart = rangeFrom && value ? (rangeFrom < value ? rangeFrom : value) : "";
  const spanEnd = rangeFrom && value ? (rangeFrom < value ? value : rangeFrom) : "";

  function shiftFocus(days: number) {
    const from = parse(focused) ?? new Date();
    const next = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
    setWalked(iso(next));
    setCursorTo(next);
    requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus());
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (event.key in moves) { event.preventDefault(); shiftFocus(moves[event.key]); return; }
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      const from = parse(focused) ?? new Date();
      const next = new Date(from.getFullYear(), from.getMonth() + (event.key === "PageUp" ? -1 : 1), from.getDate());
      setWalked(iso(next));
      setCursorTo(next);
      requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus());
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!blocked(focused)) onChange(focused);
    }
  }

  return (
    <div className="calendar" role="group" aria-label={label}>
      <div className="calendar-head">
        <button className="calendar-step" type="button" aria-label="Previous month" title="Previous month"
          onClick={() => setMonthOffset(monthOffset - 1)}>‹</button>
        <strong aria-live="polite">{monthLabel}</strong>
        <button className="calendar-step" type="button" aria-label="Next month" title="Next month"
          onClick={() => setMonthOffset(monthOffset + 1)}>›</button>
      </div>
      <div className="calendar-weekdays" aria-hidden="true">
        {WEEKDAYS.map((day) => <span key={day}>{day.slice(0, 2)}</span>)}
      </div>
      <div className="calendar-grid" role="grid" aria-label={monthLabel} ref={grid} onKeyDown={onKeyDown}>
        {days.map((date) => {
          const day = iso(date);
          const outside = date.getMonth() !== cursor.getMonth();
          const isBlocked = blocked(day);
          const inSpan = spanStart !== "" && day > spanStart && day < spanEnd;
          const classes = [
            "calendar-day",
            day === value ? "selected" : "",
            day === today ? "today" : "",
            outside ? "outside" : "",
            inSpan ? "in-span" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={day} type="button" className={classes} role="gridcell" disabled={isBlocked}
              aria-selected={day === value} aria-current={day === today ? "date" : undefined}
              tabIndex={day === focused ? 0 : -1}
              onFocus={() => setWalked(day)}
              onClick={() => onChange(day)}
              title={new Intl.DateTimeFormat("en", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(date)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <div className="calendar-foot">
        <button className="chip" type="button" disabled={blocked(today)} onClick={() => onChange(today)}>Today</button>
        {rangeFrom ? <span className="calendar-note">Range starts {new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(parse(rangeFrom) ?? new Date())}</span> : null}
      </div>
    </div>
  );
}
